/* Stripe Checkout page — rendered after /pay returns provider:"stripe".
   Receives client_secret + appointment_id via location state (or URL params after redirect-back).
*/
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

let _stripePromise = null;
function getStripe(pk) {
  if (!_stripePromise && pk) _stripePromise = loadStripe(pk);
  return _stripePromise;
}

export default function StripeCheckout() {
  const { id: appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passed = location.state || {};

  const [config, setConfig] = useState(null);
  const [intent, setIntent] = useState(passed.intent || null);
  const [loading, setLoading] = useState(!intent);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    api.get("/payments/public-config").then((r) => setConfig(r.data));
  }, []);

  useEffect(() => {
    if (intent) return;
    (async () => {
      try {
        const { data } = await api.post(`/patient/appointments/${appointmentId}/pay`);
        if (data.provider !== "stripe") {
          // Stripe was disabled between booking and checkout — fall back
          toast("Demo payment ran instead", { description: "Stripe wasn't enabled." });
          navigate("/patient/appointments");
          return;
        }
        setIntent(data);
      } catch (e) {
        setErrMsg(apiError(e, "Failed to start checkout"));
      } finally {
        setLoading(false);
      }
    })();
  }, [appointmentId, intent, navigate]);

  const stripePromise = useMemo(
    () => (config?.stripe_publishable_key ? getStripe(config.stripe_publishable_key) : null),
    [config?.stripe_publishable_key]
  );

  const options = useMemo(() => (
    intent?.client_secret
      ? { clientSecret: intent.client_secret, appearance: { theme: "night", variables: { colorPrimary: "#14b8a6" } } }
      : null
  ), [intent?.client_secret]);

  if (loading) {
    return <div className="text-sm text-mb-fg2" data-testid="stripe-loading">Starting Stripe checkout…</div>;
  }
  if (errMsg) {
    return <div className="text-sm text-red-400" data-testid="stripe-error">{errMsg}</div>;
  }
  if (!stripePromise || !options) {
    return <div className="text-sm text-mb-fg2">Stripe is unavailable. Please try again.</div>;
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="stripe-checkout-page">
      <button onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-[0.2em] text-mb-fg2 hover:text-zinc-200 inline-flex items-center gap-2"
        data-testid="stripe-back">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ secure payment</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Complete payment</h1>
        <div className="mt-2 text-sm text-mb-fg2">
          {formatCurrency(intent.amount, { decimals: true })} · {intent.currency?.toUpperCase()} ·
          methods: {intent.payment_methods?.join(", ")}
        </div>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-6">
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm appointmentId={appointmentId} mode={intent.mode} amount={intent.amount} />
        </Elements>
      </div>

      <div className="text-[10px] uppercase tracking-[0.3em] text-mb-fg2 inline-flex items-center gap-2">
        <ShieldCheck className="h-3 w-3 text-teal-400" />
        Powered by Stripe · {intent.mode === "live" ? "Live" : "Test"} mode
      </div>
    </div>
  );
}

function CheckoutForm({ appointmentId, mode, amount }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrMsg("");
    const returnUrl = `${window.location.origin}/patient/appointments/${appointmentId}/confirm`;
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });
    if (result.error) {
      setErrMsg(result.error.message || "Payment failed");
      toast.error(result.error.message || "Payment failed");
      setSubmitting(false);
      return;
    }
    // No redirect happened (synchronous success) — poll appointment status
    toast.success("Payment submitted — confirming with the hospital…");
    navigate(`/patient/appointments/${appointmentId}/confirm`);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="stripe-checkout-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {errMsg && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="stripe-form-error">
          {errMsg}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="stripe-submit"
        className="bg-teal-500 hover:bg-teal-400 text-zinc-950 w-full"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${formatCurrency(amount, { decimals: true })}`}
      </Button>
    </form>
  );
}
